import type { CompanyArticles } from "@/modules/companies/client/api/types"
import type { I18nLocale } from "@/modules/i18n/types"

import { useQuery } from "@tanstack/react-query"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import {
  COMPANY_ARTICLES_QUERY_KEY,
  getCompanyArticles,
} from "@/modules/companies/client/api/endpoints/companyArticles"
import { QUERY_CACHE_TIME, QUERY_STALE_TIME } from "@/modules/companies/client/constants/enums"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { stripTagClientSide } from "@/modules/companies/client/utils/stripTagClientSide"
import { DEFAULT_LOCALE } from "@/modules/locales/constants"

export type CompanyBoostedArticlesFetch = {
  dataCompanyBoostedArticles?: CompanyArticles
  isLoadingCompanyBoostedArticles: boolean
  isSuccessCompanyBoostedArticles: boolean
  isErrorCompanyBoostedArticles: boolean
}

export const useCompanyBoostedArticlesFetch = (): CompanyBoostedArticlesFetch => {
  const { dataCompany } = useCompanyFetch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const locale = (dataCompany?.page.locale as I18nLocale) || DEFAULT_LOCALE

  const schoolIds = dataCareerCenter && dataCareerCenter.intranet ? [dataCareerCenter.id] : null

  const {
    data: dataCompanyBoostedArticles,
    isPending: isLoadingCompanyBoostedArticles,
    isSuccess: isSuccessCompanyBoostedArticles,
    isError: isErrorCompanyBoostedArticles,
  } = useQuery({
    enabled: !!dataCompany?.id,
    gcTime: QUERY_CACHE_TIME,
    queryFn: () => {
      return getCompanyArticles({
        params: {
          boosted_only: true,
          company_id: dataCompany?.id as string,
          locales: [locale],
          page_limit: 1,
          page_offset: 0,
          school_ids: schoolIds || undefined,
        },
        sanitize: stripTagClientSide,
      })
    },
    queryKey: [COMPANY_ARTICLES_QUERY_KEY, dataCompany?.id, locale, "boosted_only", schoolIds],
    staleTime: QUERY_STALE_TIME,
  })

  return {
    dataCompanyBoostedArticles,
    isErrorCompanyBoostedArticles,
    isLoadingCompanyBoostedArticles,
    isSuccessCompanyBoostedArticles,
  }
}
